import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid,
  ThemeProvider,
  IconButton,
  Typography,
  Divider
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import theme from '../../styles/themes/default';
import Gallery from '../../components/gameBicycle/Gallery';

import slide1 from '../../assets/images/bicycle_game/p1.png';
import slide2 from '../../assets/images/bicycle_game/p2.png';
import slide3 from '../../assets/images/bicycle_game/p3.png';

interface SlideData {
  image: string;
}
const slidesData: SlideData[] = [
  {
    image: slide1
  },
  {
    image: slide2
  },
  {
    image: slide3
  },
];

interface SliderProps {
  interval?: number;
  slidesToShow?: number;
}

const Slider: React.FC<SliderProps> = ({ interval = 5000, slidesToShow = 3 }) => {
  const [startIndex, setStartIndex] = useState(0);
  const totalSlides = slidesData.length;
  
  // Determinăm câte slide-uri să afișăm pe ecrane de diferite dimensiuni
  const visibleSlides = Math.min(slidesToShow, totalSlides);
  
  const handleNext = () => {
    setStartIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      return newIndex >= totalSlides ? 0 : newIndex;
    });
  };

  const handleBack = () => {
    setStartIndex((prevIndex) => {
      const newIndex = prevIndex - 1;
      return newIndex < 0 ? totalSlides - 1 : newIndex;
    });
  };

  // Auto-play effect
  useEffect(() => {
    if (!interval || interval <= 0) return;
    
    const timer = setInterval(() => {
      handleNext();
    }, interval);
    
    return () => {
      clearInterval(timer);
    };
  }, [interval]);

  // Calculăm care slide-uri sunt vizibile în momentul curent
  const getVisibleSlides = () => {
    const slides = [];
    for (let i = 0; i < visibleSlides; i++) {
      const index = (startIndex + i) % totalSlides;
      slides.push(slidesData[index]);
    }
    return slides;
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Container exterior pentru fundalul maro pe tot ecranul */}
      <Box sx={{ 
        width: '100%',
        backgroundColor: '#4a3824',
        padding: { xs: '0.5rem', sm: '1rem', md: '2rem' },
        boxSizing: 'border-box',
        overflow: 'hidden',
        maxWidth: '100vw'
      }}>
        <Typography 
            variant="h4" 
            sx={{ 
              color: '#e9d4a8',
              //fontWeight: 'bold',
              textAlign: 'center',
              mb: { xs: 2, sm: 3, md: 5 },
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
              padding: '0 0.5rem',
              fontFamily: theme.fonts.poplar
            }}
        >
            CAUZA PE CARE O SUSȚINEM ÎMPREUNĂ
        </Typography>
        
        {/* Container interior centrat pentru poze și săgeți */}
        <Box sx={{
          maxWidth: '95%',
          width: '95%',
          margin: '0 auto',
          position: 'relative',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <Grid 
            container 
            spacing={{ xs: 0, sm: 1, md: 2 }}
            sx={{ 
              width: '100%', 
              margin: 0,
              padding: 0,
              boxSizing: 'border-box'
            }}
          >
            {getVisibleSlides().map((slide, index) => (
              <Grid 
                item 
                xs={12}
                sm={12}
                md={12 / visibleSlides} 
                key={index}
                sx={{
                  transition: 'all 0.5s ease-in-out',
                  display: {
                    xs: index === 0 ? 'block' : 'none',
                    sm: index === 0 ? 'block' : 'none',
                    md: 'block'
                  },
                  boxSizing: 'border-box',
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '95%',
                    height: { xs: '220px', sm: '250px', md: '400px' },
                    backgroundImage: `url(${slide.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    borderRadius: { xs: 1, md: 2 },
                    transition: 'transform 0.3s ease',
                    margin: { xs: '0.5rem auto', md: 0 },
                    border: '1px solid white',
                    maxWidth: { xs: 'calc(100% - 1rem)', md: '100%' },
                    boxSizing: 'border-box',
                    '&:hover': {
                      transform: { xs: 'none', md: 'scale(1.03)' },
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>
          
          {/* Butoane de navigare relative la containerul interior */}
          {totalSlides > 1 && (
            <>
              <IconButton
                onClick={handleBack}
                sx={{
                  position: 'absolute',
                  left: 5,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  zIndex: 2,
                  width: { xs: '26px', sm: '35px', md: '40px' },
                  height: { xs: '26px', sm: '35px', md: '40px' },
                  minWidth: { xs: '26px' },
                  padding: 0
                }}
              >
                <KeyboardArrowLeft fontSize="small" />
              </IconButton>
              
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 5,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  zIndex: 2,
                  width: { xs: '26px', sm: '35px', md: '40px' },
                  height: { xs: '26px', sm: '35px', md: '40px' },
                  minWidth: { xs: '26px' },
                  padding: 0 
                }}
              >
                <KeyboardArrowRight fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
        <Typography 
              variant="h4" 
              sx={{ 
                color: '#e9d4a8',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: { xs: 3, sm: 3, md: 3 },
                mt: 5,
                fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2rem' },
                padding: '0 0.5rem'
              }}
          >
              Împreună cu Act For Tomorrow iluminăm alei în parcuri din orașele
          </Typography>

          {/* Listă neordonată de orașe */}
          <Box 
            sx={{ 
              display: 'flex',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <Box component="ul" sx={{ 
              color: '#e9d4a8',
              textAlign: 'center',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 'bold',
              margin: '0 auto',
              fontFamily: theme.fonts.apex,
              listStyle: 'none',
              pl: 2,
              '& li': {
                marginBottom: 1,
              }
            }}>
              <li>București</li>
              <li>Constanta</li>
              <li>Timișoara</li>
              <li>Brașov</li>
              <li>Suceava</li>
            </Box>
          </Box>
        <Divider sx={{ my: 6, borderColor:'#e9d4a8', borderWidth: 3 }} />
        <Gallery/>
      </Box>
    </ThemeProvider>
  );
};

export default Slider;